import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api, type Entry, type Subscription } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import EntryCard from '@/components/EntryCard';
import KpiCard from '@/components/dashboard/KpiCard';
import SentimentAreaChart from '@/components/dashboard/SentimentAreaChart';
import PremiumModal from '@/components/PremiumModal';
import OnboardingOverlay, { markOnboardingRecordStep } from '@/components/OnboardingOverlay';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, Calendar, Flame, Target, BookOpen, Crown, FileText, PartyPopper, Infinity, Palette, Image, Tag, X } from 'lucide-react';
import { format, subYears, subDays, startOfWeek } from 'date-fns';
import { useNavigate } from 'react-router-dom';

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [recentEntries, setRecentEntries] = useState<Entry[]>([]);
  const [chartEntries, setChartEntries] = useState<Entry[]>([]);
  const [yearAgoEntries, setYearAgoEntries] = useState<Entry[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [totalCharacters, setTotalCharacters] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);

  // Handle payment success/cancel URL params
  useEffect(() => {
    const payment = searchParams.get('payment');
    if (payment === 'success') {
      searchParams.delete('payment');
      setSearchParams(searchParams, { replace: true });
      setShowSuccessOverlay(true);

      // Webhook 遅延対策: 2秒間隔で最大10回ポーリング
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const sub = await api.getSubscription();
          if (sub.premium) {
            setSubscription(sub);
            clearInterval(poll);
          }
        } catch { /* ignore */ }
        if (attempts >= 10) clearInterval(poll);
      }, 2000);

      return () => clearInterval(poll);
    } else if (payment === 'cancel') {
      searchParams.delete('payment');
      setSearchParams(searchParams, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const load = async () => {
      try {
        const [entries, allEntries, yearAgo, sub, statsRes] = await Promise.allSettled([
          api.getEntries({ limit: 5 }),
          api.getEntries({ limit: 60 }),
          api.getEntriesByDate(format(subYears(new Date(), 1), 'yyyy-MM-dd')),
          api.getSubscription(),
          api.getStats(),
        ]);

        if (entries.status === 'fulfilled') {
          setRecentEntries(entries.value);
        }
        if (allEntries.status === 'fulfilled') {
          setChartEntries(allEntries.value);
        }
        if (yearAgo.status === 'fulfilled') {
          setYearAgoEntries(yearAgo.value);
        }
        if (sub.status === 'fulfilled') {
          setSubscription(sub.value);
          if (!sub.value.premium && sub.value.remainingSeconds <= 0) {
            setShowPremiumModal(true);
          }
        }
        if (statsRes.status === 'fulfilled') {
          setTotalCharacters(statsRes.value.totalCharacters);
        }

      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Calculate streak and weekly progress from chartEntries
  const { streak, weeklyCount } = (() => {
    if (chartEntries.length === 0) return { streak: 0, weeklyCount: 0 };

    // Get unique dates that have entries
    const entryDates = new Set(
      chartEntries.map(e => e.date || (e.created_at ? e.created_at.split('T')[0] : ''))
        .filter(Boolean)
    );

    // Calculate streak: consecutive days ending at today (or yesterday)
    let currentStreak = 0;
    const today = new Date();
    // Check if today has an entry; if not, start from yesterday
    const todayStr = format(today, 'yyyy-MM-dd');
    let checkDate = entryDates.has(todayStr) ? today : subDays(today, 1);

    for (let i = 0; i < 365; i++) {
      const dateStr = format(checkDate, 'yyyy-MM-dd');
      if (entryDates.has(dateStr)) {
        currentStreak++;
        checkDate = subDays(checkDate, 1);
      } else {
        break;
      }
    }

    // Calculate weekly progress using user's preferred week start day
    const weekStartDay = localStorage.getItem('weekStartsOn') === '0' ? 0 : 1;
    const weekStart = startOfWeek(today, { weekStartsOn: weekStartDay as 0 | 1 });
    let weekCount = 0;
    for (let i = 0; i < 7; i++) {
      const wd = new Date(weekStart);
      wd.setDate(wd.getDate() + i);
      if (entryDates.has(format(wd, 'yyyy-MM-dd'))) weekCount++;
    }

    return { streak: currentStreak, weeklyCount: weekCount };
  })();

  // Keyboard shortcut: Ctrl+N → new recording
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        e.preventDefault();
        navigate('/record');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-pulse text-muted-foreground">読み込み中...</div>
      </div>
    );
  }

  // Character count display value & suffix
  const charValue = totalCharacters >= 10000 ? +(totalCharacters / 10000).toFixed(1) : totalCharacters;
  const charSuffix = totalCharacters >= 10000 ? '万' : '';
  const charDecimals = totalCharacters >= 10000 ? 1 : 0;

  return (
    <div className="space-y-6">
      {/* Welcome + Date */}
      <div>
        <h1 className="text-lg sm:text-2xl font-bold truncate">
          {(() => {
            const h = new Date().getHours();
            if (h < 5) return 'おつかれさま';
            if (h < 11) return 'おはよう';
            if (h < 17) return 'こんにちは';
            return 'こんばんは';
          })()}、{user?.name || 'ゲスト'}さん
        </h1>
        <p className="text-sm text-muted-foreground">{format(new Date(), 'yyyy年M月d日')}</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <KpiCard icon={Flame} value={streak} suffix="日" label="継続" color="orange" index={0} />
        <KpiCard icon={Target} value={weeklyCount} suffix="/7" label="今週" color="emerald" index={1} />
        <KpiCard icon={FileText} value={charValue} suffix={charSuffix} label="文字数" color="blue" index={2} decimals={charDecimals} />
      </div>

      {/* Charts: Sentiment / Mood tabbed card */}
      <SentimentAreaChart entries={chartEntries} />

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3">
        <Link to="/timeline">
          <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="flex flex-col items-center justify-center gap-2 p-4 sm:p-5">
              <Calendar className="h-8 w-8 sm:h-9 sm:w-9 text-primary" />
              <span className="text-sm font-medium">過去のひとりごと</span>
            </CardContent>
          </Card>
        </Link>
        <Link to="/review">
          <Card className="hover:shadow-md transition-shadow cursor-pointer h-full border-purple-500/30 bg-purple-500/5">
            <CardContent className="flex flex-col items-center justify-center gap-2 p-4 sm:p-5">
              <BookOpen className="h-8 w-8 sm:h-9 sm:w-9 text-purple-500" />
              <span className="text-sm font-medium">振り返り</span>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Year ago */}
      {yearAgoEntries.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">1年前の今日</CardTitle>
            <CardDescription>
              {format(subYears(new Date(), 1), 'yyyy年M月d日')}の記録
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {yearAgoEntries.map(entry => (
                <EntryCard key={entry.id} entry={entry} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Remaining time / Premium upgrade */}
      {subscription && !subscription.premium && subscription.remainingSeconds <= 300 && subscription.remainingSeconds > 0 && (
        <Card className="border-amber-500/30 bg-amber-500/5 cursor-pointer" onClick={() => setShowPremiumModal(true)}>
          <CardContent className="flex items-center justify-between gap-2 p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <Crown className="h-5 w-5 text-amber-500 shrink-0" />
              <div className="min-w-0">
                <p className="font-medium text-sm truncate">残り録音: {Math.max(1, Math.ceil(subscription.remainingSeconds / 60))}分</p>
                <p className="text-xs text-muted-foreground">プレミアムで無制限に</p>
              </div>
            </div>
            <Button size="sm" variant="outline" className="shrink-0">詳細</Button>
          </CardContent>
        </Card>
      )}

      {/* Recent entries */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div>
            <CardTitle className="text-base sm:text-lg">最近のエントリ</CardTitle>
          </div>
          <Link to="/timeline">
            <Button variant="ghost" size="sm">すべて見る</Button>
          </Link>
        </CardHeader>
        <CardContent>
          {recentEntries.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Mic className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>まだエントリがありません</p>
              <Link to="/record">
                <Button variant="link">最初のエントリを作成する</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-2 sm:space-y-3">
              {recentEntries.slice(0, typeof window !== 'undefined' && window.innerWidth < 640 ? 3 : 5).map(entry => (
                <EntryCard key={entry.id} entry={entry} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <PremiumModal
        open={showPremiumModal}
        onOpenChange={setShowPremiumModal}
        onActivated={() => {
          api.getSubscription().then(setSubscription).catch(() => {});
        }}
      />

      {/* Fixed FAB */}
      <Link
        to="/record"
        onClick={() => markOnboardingRecordStep()}
        className="fixed bottom-20 right-4 lg:bottom-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-colors"
      >
        <Mic className="h-6 w-6" />
      </Link>

      <OnboardingOverlay isNewUser={recentEntries.length === 0 && !loading} />

      {/* Payment success overlay */}
      <AnimatePresence>
        {showSuccessOverlay && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setShowSuccessOverlay(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="relative w-full max-w-sm rounded-2xl bg-background border shadow-2xl p-6 space-y-5"
              onClick={e => e.stopPropagation()}
            >
              <button
                onClick={() => setShowSuccessOverlay(false)}
                className="absolute top-3 right-3 rounded-full p-1 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              >
                <X className="h-5 w-5" />
              </button>

              <div className="text-center space-y-2">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', delay: 0.2, duration: 0.6 }}
                  className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-amber-500/10 mx-auto"
                >
                  <PartyPopper className="h-8 w-8 text-amber-500" />
                </motion.div>
                <h2 className="text-xl font-bold">ようこそ、プレミアムへ！</h2>
                <p className="text-sm text-muted-foreground">すべての機能が解放されました</p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {([
                  { icon: Infinity, label: '録音時間無制限', color: 'text-blue-500' },
                  { icon: Palette, label: '全テーマ開放', color: 'text-pink-500' },
                  { icon: Image, label: '画像添付', color: 'text-orange-500' },
                  { icon: Tag, label: 'タグ50個', color: 'text-purple-500' },
                  { icon: BookOpen, label: '振り返り強化', color: 'text-emerald-500' },
                  { icon: Crown, label: '優先サポート', color: 'text-amber-500' },
                ] as const).map(({ icon: Icon, label, color }) => (
                  <div key={label} className="flex items-center gap-2 rounded-lg bg-muted/40 px-3 py-2">
                    <Icon className={`h-4 w-4 shrink-0 ${color}`} />
                    <span className="text-xs font-medium">{label}</span>
                  </div>
                ))}
              </div>

              <Button
                className="w-full"
                onClick={() => setShowSuccessOverlay(false)}
              >
                使い始める
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
